import { useCallback, useEffect, useState } from "react";
import { Home } from "./components/Home";
import { Reader } from "./components/Reader";
import {
  consumeLayoutMigrationNotice,
  getProject,
  listProjects,
  restoreProjectBackup,
  saveProject
} from "./lib/db";
import { parsePdf, type ParseProgress } from "./lib/pdf";
import type { LocalProject, ProjectSummary } from "./types";

export function App() {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [activeProject, setActiveProject] = useState<LocalProject | null>(null);
  const [busyLabel, setBusyLabel] = useState("");
  const [parseProgress, setParseProgress] = useState<ParseProgress | null>(null);
  const [error, setError] = useState("");
  const [migrationNotice, setMigrationNotice] = useState("");

  const refreshProjects = useCallback(async () => {
    setProjects(await listProjects());
    if (consumeLayoutMigrationNotice()) {
      setMigrationNotice(
        "版面识别已升级到 v2。旧项目已清理，请重新上传原始 PDF。"
      );
    }
  }, []);

  useEffect(() => {
    void refreshProjects();
  }, [refreshProjects]);

  useEffect(() => {
    if (!activeProject) return;
    const timeout = window.setTimeout(() => {
      void saveProject(activeProject);
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [activeProject]);

  const openProject = useCallback(async (id: string) => {
    setBusyLabel("正在打开本地项目…");
    setError("");
    try {
      const project = await getProject(id);
      if (!project) throw new Error("找不到这个本地项目，可能已被清除。");
      setActiveProject(project);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "无法打开项目。");
    } finally {
      setBusyLabel("");
    }
  }, []);

  const importPdf = useCallback(async (file: File) => {
    setBusyLabel("正在读取 PDF 文本层…");
    setParseProgress(null);
    setError("");
    try {
      const existingBytes = await file.arrayBuffer();
      const digest = await crypto.subtle.digest("SHA-256", existingBytes);
      const id = Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
      const existing = await getProject(id);
      if (existing) {
        setActiveProject(existing);
        return;
      }

      const project = await parsePdf(file, (progress) => {
        setParseProgress(progress);
        setBusyLabel(
          `正在分析版式 ${progress.currentPage} / ${progress.pageCount}`
        );
      });
      await saveProject(project);
      setActiveProject(project);
      await refreshProjects();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "PDF 解析失败。");
    } finally {
      setBusyLabel("");
      setParseProgress(null);
    }
  }, [refreshProjects]);

  const importBackup = useCallback(
    async (file: File) => {
      setBusyLabel("正在恢复项目备份…");
      setError("");
      try {
        const project = await restoreProjectBackup(file);
        await refreshProjects();
        setActiveProject(project);
      } catch (reason) {
        setError(
          reason instanceof Error ? reason.message : "项目备份恢复失败。"
        );
      } finally {
        setBusyLabel("");
      }
    },
    [refreshProjects]
  );

  if (activeProject) {
    return (
      <Reader
        project={activeProject}
        onChange={setActiveProject}
        onBack={async () => {
          await saveProject(activeProject);
          setActiveProject(null);
          await refreshProjects();
        }}
      />
    );
  }

  return (
    <>
      <Home
        projects={projects}
        error={error}
        notice={migrationNotice}
        onDismissError={() => setError("")}
        onDismissNotice={() => setMigrationNotice("")}
        onOpen={openProject}
        onPdf={importPdf}
        onBackup={importBackup}
        onProjectsChanged={refreshProjects}
      />
      {busyLabel && (
        <div className="busy-overlay" role="status" aria-live="polite">
          <div className="busy-card">
            <span className="spinner" />
            <strong>{busyLabel}</strong>
            {parseProgress && (
              <div className="progress-track" aria-hidden="true">
                <span
                  style={{
                    width: `${Math.round(
                      (parseProgress.currentPage / parseProgress.pageCount) * 100
                    )}%`
                  }}
                />
              </div>
            )}
            <small>文件只在当前浏览器中处理</small>
          </div>
        </div>
      )}
    </>
  );
}
